import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { toChipState, toPendingUpload } from './attachmentChipState';
import { resolveAttachmentMimeType } from './attachmentMimeType';
import { getPlatformUserId } from '../../../utils/utils';
import { useAppSelector } from '../../../app/hooks';
import { getSignedUrl } from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { MAGIC_UPLOAD_DEFAULT_CATEGORY_ID } from '../../../common/MagicUpload/Utils/MagicUploadConstants';
import { claimAttachments, saveChatFileMetadata, uploadToS3WithProgress } from './attachmentApi';
import {
    CHAT_ATTACHMENT_BLOCKED_EXTENSIONS,
    CHAT_ATTACHMENT_BLOCKED_MIME_TYPES,
    CHAT_ATTACHMENT_COPY,
    CHAT_ATTACHMENT_MAX_FILES,
    CHAT_ATTACHMENT_MAX_FILE_SIZE_BYTES,
    CHAT_ATTACHMENT_STATUS,
} from './constants';
import { ChatAttachmentChip, ClaimAttachmentsResponse } from './types';

interface UseChatAttachmentsParams {
    /** The conversation the composer is bound to; null before the first claim of a new chat. */
    conversationId: string | null | undefined;
    source: 'lead' | 'portfolio';
    /** Company org for the leads surface; undefined on portfolio (no org header is sent). */
    orgId?: string;
    /** Binds the conversation id returned by the claim so the chat POST carries it. */
    onConversationBound: (conversationId: string) => void;
    getCompanyName?: (orgId: string | null) => string | undefined;
}

const createTempId = (file: File, index: number) => `temp_${Date.now()}_${index}_${file.name}`;

/** An archive by extension OR by MIME type — either may be the only one present. */
export const isArchiveFile = (file: File) => {
    const name = file.name.toLowerCase();
    return (
        CHAT_ATTACHMENT_BLOCKED_EXTENSIONS.some((extension) => name.endsWith(extension)) ||
        (CHAT_ATTACHMENT_BLOCKED_MIME_TYPES as readonly string[]).includes(file.type.toLowerCase())
    );
};

/** Composer-side attachment state machine: pick -> limits -> mnemosyne upload -> batch claim -> chip state. */
export const useChatAttachments = ({ conversationId, source, orgId, onConversationBound, getCompanyName }: UseChatAttachmentsParams) => {
    const userId = useAppSelector((state) => state.user?.userData?.userId);
    const mnemosyneOrgId = useAppSelector((state) => state.user?.domainMetadata?.investorOrgId);
    const investorCategoryId = useAppSelector((state) => state.investor?.config?.dataConfig?.staging?.categoryId);
    const companyCategoryId = useAppSelector((state) => state.company?.config?.dataConfig?.staging?.categoryId);
    const categoryId = investorCategoryId || companyCategoryId || MAGIC_UPLOAD_DEFAULT_CATEGORY_ID;

    const [items, setItemsState] = useState<ChatAttachmentChip[]>([]);
    const itemsRef = useRef<ChatAttachmentChip[]>([]);
    const boundConversationRef = useRef(conversationId ?? null);
    const boundOrgRef = useRef(orgId ?? null);

    const setItems = useCallback((update: (previous: ChatAttachmentChip[]) => ChatAttachmentChip[]) => {
        itemsRef.current = update(itemsRef.current);
        setItemsState(itemsRef.current);
    }, []);

    const patchItem = useCallback(
        (id: string, patch: Partial<ChatAttachmentChip>) =>
            setItems((previous) => previous.map((item) => (item.id === id ? { ...item, ...patch } : item))),
        [setItems],
    );

    // Claimed rows belong to one conversation: leaving it (New Chat, another conversation) drops the chips.
    useEffect(() => {
        if ((conversationId ?? null) === boundConversationRef.current) return;
        boundConversationRef.current = conversationId ?? null;
        setItems(() => []);
    }, [conversationId, setItems]);

    // Chips are bound to the company pinned when they were claimed: a switch drops them, even while no
    // conversation is bound yet (the conversation effect above cannot see that window).
    useEffect(() => {
        if ((orgId ?? null) === boundOrgRef.current) return;
        boundOrgRef.current = orgId ?? null;
        setItems(() => []);
    }, [orgId, setItems]);

    const uploadOne = useCallback(
        async (file: File, tempId: string) => {
            // Read at upload time, before anything reaches storage; an empty id fails Athena's ownership gate.
            const uploadedBy = getPlatformUserId();
            if (!uploadedBy) throw new Error(CHAT_ATTACHMENT_COPY.SESSION_INCOMPLETE);
            const contentType = resolveAttachmentMimeType(file);
            const signResponse = await getSignedUrl(
                { file: { name: file.name, size: file.size, type: contentType } },
                { assesseeOrgId: orgId, categoryId, mnemosyneOrgId },
            );
            if (!signResponse?.signedUrl) throw new Error(CHAT_ATTACHMENT_COPY.UPLOAD_FAILED);
            await uploadToS3WithProgress(signResponse.signedUrl, file, contentType, (progress) => patchItem(tempId, { progress }));
            const mnemosyneDocId = await saveChatFileMetadata({
                file,
                contentType,
                categoryId,
                mnemosyneOrgId,
                assesseeOrgId: orgId,
                uploadedBy,
            });
            patchItem(tempId, { status: CHAT_ATTACHMENT_STATUS.REGISTERING, progress: 100, mnemosyneDocId });
            return mnemosyneDocId;
        },
        [orgId, categoryId, mnemosyneOrgId, patchItem],
    );

    const applyClaim = useCallback(
        (response: ClaimAttachmentsResponse) => {
            setItems((previous) =>
                previous.map((item) => {
                    const claimed = response.attachments?.find((attachment) => attachment.mnemosyne_doc_id === item.mnemosyneDocId);
                    if (claimed) {
                        // Pre-send nothing is filed, so whatever status the response carries, the chip is only ever ready.
                        return { ...item, ...toChipState(claimed, getCompanyName), status: CHAT_ATTACHMENT_STATUS.READY, claimed: true };
                    }
                    const rejected = response.rejected?.find((entry) => entry.mnemosyne_doc_id === item.mnemosyneDocId);
                    if (rejected) {
                        return {
                            ...item,
                            status: CHAT_ATTACHMENT_STATUS.FAILED,
                            failureReason: rejected.detail || rejected.reason,
                            claimed: true,
                        };
                    }
                    return item;
                }),
            );
            if (response.conversation_id && response.conversation_id !== boundConversationRef.current) {
                boundConversationRef.current = response.conversation_id;
                onConversationBound(response.conversation_id);
            }
        },
        [getCompanyName, onConversationBound, setItems],
    );

    const addFiles = useCallback(
        async (selectedFiles: File[]) => {
            if (!selectedFiles.length) return;
            const archives = selectedFiles.filter(isArchiveFile);
            if (archives.length) {
                toast.error(`${CHAT_ATTACHMENT_COPY.ARCHIVE_NOT_SUPPORTED}: ${archives.map((archive) => archive.name).join(', ')}`);
            }
            const files = selectedFiles.filter((file) => !isArchiveFile(file));
            if (!files.length) return;
            const pendingCount = itemsRef.current.filter((item) => item.status !== CHAT_ATTACHMENT_STATUS.FAILED).length;
            if (pendingCount + files.length > CHAT_ATTACHMENT_MAX_FILES) {
                toast.error(CHAT_ATTACHMENT_COPY.TOO_MANY);
                return;
            }
            const oversized = files.find((file) => file.size > CHAT_ATTACHMENT_MAX_FILE_SIZE_BYTES);
            if (oversized) {
                toast.error(`${CHAT_ATTACHMENT_COPY.TOO_LARGE}: ${oversized.name}`);
                return;
            }

            const batch = files.map((file, index) => ({ file, tempId: createTempId(file, index) }));
            setItems((previous) => [
                ...previous,
                ...batch.map(({ file, tempId }) => ({
                    id: tempId,
                    fileName: file.name,
                    status: CHAT_ATTACHMENT_STATUS.UPLOADING,
                    progress: 0,
                    mimeType: resolveAttachmentMimeType(file),
                    sizeBytes: file.size,
                })),
            ]);

            const outcomes = await Promise.all(
                batch.map(async ({ file, tempId }) => {
                    try {
                        await uploadOne(file, tempId);
                        return tempId;
                    } catch (error) {
                        const reason = error instanceof Error && error.message ? error.message : CHAT_ATTACHMENT_COPY.UPLOAD_FAILED;
                        patchItem(tempId, { status: CHAT_ATTACHMENT_STATUS.FAILED, failureReason: reason });
                        return null;
                    }
                }),
            );

            // A chip removed while its upload ran must not be claimed.
            const toClaim = itemsRef.current.filter((item) => outcomes.includes(item.id) && item.mnemosyneDocId);
            if (!toClaim.length) return;
            try {
                const response = await claimAttachments(
                    {
                        conversation_id: boundConversationRef.current,
                        source,
                        attachments: toClaim.map((item) => ({
                            mnemosyne_doc_id: item.mnemosyneDocId as string,
                            file_name: item.fileName,
                            mime_type: item.mimeType,
                            size_bytes: item.sizeBytes,
                        })),
                    },
                    userId,
                    orgId,
                );
                applyClaim(response);
            } catch (error) {
                const claimIds = new Set(toClaim.map((item) => item.id));
                setItems((previous) =>
                    previous.map((item) =>
                        claimIds.has(item.id)
                            ? { ...item, status: CHAT_ATTACHMENT_STATUS.FAILED, failureReason: CHAT_ATTACHMENT_COPY.UPLOAD_FAILED }
                            : item,
                    ),
                );
            }
        },
        [applyClaim, orgId, patchItem, setItems, source, uploadOne, userId],
    );

    // Pure client state: Athena holds nothing until the chat send, so there is nothing to delete.
    const removeItem = useCallback((id: string) => setItems((previous) => previous.filter((entry) => entry.id !== id)), [setItems]);

    const clear = useCallback(() => setItems(() => []), [setItems]);

    const attachmentDocIds = useMemo(
        () =>
            items
                .filter((item) => item.claimed && item.status !== CHAT_ATTACHMENT_STATUS.FAILED)
                .map((item) => item.mnemosyneDocId as string),
        [items],
    );
    // Same filter as attachmentDocIds so the optimistic cards and the ids the send
    // carries can never disagree.
    const pendingUploads = useMemo(
        () => items.filter((item) => item.claimed && item.status !== CHAT_ATTACHMENT_STATUS.FAILED).map(toPendingUpload),
        [items],
    );
    const allSettled = useMemo(() => items.every((item) => item.claimed || item.status === CHAT_ATTACHMENT_STATUS.FAILED), [items]);

    return { items, addFiles, removeItem, clear, attachmentDocIds, pendingUploads, allSettled };
};
